import * as React from "react";
import { cn } from "../cn";

/**
 * Flat placeholder for async / in-flight states. Uses `bg-subtle` with a subtle
 * pulse — no shadow (resting surfaces stay flat per the design brief). Compose the
 * base `Skeleton` directly, or reach for a preset below.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("animate-pulse rounded-md bg-subtle", className)}
    />
  );
}

/** A stack of text lines; the last line is shortened to read as a paragraph. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={cn("h-3.5", i === lines - 1 ? "w-2/3" : "w-full")} />
      ))}
    </div>
  );
}

/** Card-shaped placeholder: title line plus a few body lines inside a flat frame. */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("rounded-card border border-line bg-card p-6", className)}>
      <Skeleton className="h-4 w-1/3" />
      <SkeletonText lines={3} className="mt-4" />
    </div>
  );
}

/** Table/list row placeholder: a leading avatar block and two stacked text lines. */
export function SkeletonRow({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-3.5 w-1/2" />
        <Skeleton className="h-3 w-1/3" />
      </div>
    </div>
  );
}
